/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * Module containing match components.
 * @preferred
 * @module MatchModule
 */

import {
    CUSTOM_ELEMENTS_SCHEMA,
    NgModule,
    NO_ERRORS_SCHEMA,
} from '@angular/core';

import { CommonModule } from '@angular/common';
import { VIPModule } from '@vmw/ngx-vip';

import { SharedModule } from 'ng/shared/shared.module';
import { AviFormsModule } from 'ng/modules/avi-forms/avi-forms.module';
import { DataGridModule } from 'ng/modules/data-grid/data-grid.module';
import { AviComponentRenderingModule } from 'ng/modules/avi-component-rendering';

import {
    AuthAttributeMatchComponent,
    AuthAttributeMatchReadonlyComponent,
    AuthMatchAttributeReadonlyComponent,
    AuthMatchGroupMembershipReadonlyComponent,
    BotClassMatcherComponent,
    BotClassMatcherReadonlyComponent,
    BotManagementMatchComponent,
    BotManagementMatchReadonlyComponent,
    BotTypeMatcherComponent,
    BotTypeMatcherReadonlyComponent,
    GenericHdrMatchComponent,
    GeoMatchComponent,
    GeoMatchReadonlyComponent,
    HdrMatchComponent,
    HdrMatchReadonlyComponent,
    HostHdrMatchComponent,
    HostHdrMatchReadonlyComponent,
    HostHdrMatchWithObjectsComponent,
    HttpMethodMatchComponent,
    HttpMethodMatchReadonlyComponent,
    HttpStatusMatchComponent,
    HttpStatusMatchReadonlyComponent,
    HttpVersionMatchComponent,
    HttpVersionMatchReadonlyComponent,
    IpAddrMatchComponent,
    IpAddrMatchEntryComponent,
    IpAddrMatchReadonlyComponent,
    IpReputationTypeMatchComponent,
    IpReputationTypeMatchReadonlyComponent,
    JwtClaimMatchConfigComponent,
    JwtMatchComponent,
    JwtMatchReadonlyComponent,
    MatchAdderComponent,
    MatchAdderWithObjectsComponent,
    MatchAndActionDisplayWrapperComponent,
    MatchDisplayWrapperComponent,
    MatchWrapperComponent,
    MethodMatchWithObjectsComponent,
    PathMatchWithObjectsComponent,
    PathOrQueryMatchComponent,
    PathOrQueryMatchReadonlyComponent,
    PortMatchComponent,
    PortMatchReadonlyComponent,
    ProtocolMatchReadonlyComponent,
    ProtocolTypeMatchComponent,
    ProtocolTypeMatchReadonlyComponent,
    ServiceMatchComponent,
    StringMatchComponent,
    StringMatchConfigComponent,
    StringMatchReadonlyComponent,
    TlsFingerprintMatchComponent,
    TlsFingerprintMatchReadonlyComponent,
} from './components';

import {
    ipAddrGroupCollectionProvider,
    rangeParserProvider,
} from './ajs-upgraded-providers';

const matchComponents = [
    AuthAttributeMatchComponent,
    AuthAttributeMatchReadonlyComponent,
    AuthMatchAttributeReadonlyComponent,
    AuthMatchGroupMembershipReadonlyComponent,
    BotClassMatcherComponent,
    BotClassMatcherReadonlyComponent,
    BotTypeMatcherComponent,
    BotTypeMatcherReadonlyComponent,
    BotManagementMatchComponent,
    BotManagementMatchReadonlyComponent,
    GenericHdrMatchComponent,
    HdrMatchComponent,
    HdrMatchReadonlyComponent,
    HostHdrMatchComponent,
    HostHdrMatchReadonlyComponent,
    HostHdrMatchWithObjectsComponent,
    HttpMethodMatchComponent,
    HttpMethodMatchReadonlyComponent,
    HttpStatusMatchComponent,
    HttpStatusMatchReadonlyComponent,
    HttpVersionMatchComponent,
    HttpVersionMatchReadonlyComponent,
    IpAddrMatchComponent,
    IpAddrMatchEntryComponent,
    IpAddrMatchReadonlyComponent,
    IpReputationTypeMatchComponent,
    IpReputationTypeMatchReadonlyComponent,
    JwtClaimMatchConfigComponent,
    JwtMatchComponent,
    JwtMatchReadonlyComponent,
    MatchAdderComponent,
    MatchAdderWithObjectsComponent,
    MatchAndActionDisplayWrapperComponent,
    MatchDisplayWrapperComponent,
    MatchWrapperComponent,
    MatchWrapperComponent,
    MethodMatchWithObjectsComponent,
    PathMatchWithObjectsComponent,
    PathOrQueryMatchComponent,
    PathOrQueryMatchReadonlyComponent,
    PortMatchComponent,
    PortMatchReadonlyComponent,
    ProtocolMatchReadonlyComponent,
    ProtocolTypeMatchComponent,
    ProtocolTypeMatchReadonlyComponent,
    ServiceMatchComponent,
    StringMatchConfigComponent,
    StringMatchComponent,
    StringMatchReadonlyComponent,
    GeoMatchComponent,
    GeoMatchReadonlyComponent,
    TlsFingerprintMatchComponent,
    TlsFingerprintMatchReadonlyComponent,
];

@NgModule({
    declarations: [
        ...matchComponents,
    ],
    exports: [
        AuthAttributeMatchReadonlyComponent,
        BotClassMatcherComponent,
        BotClassMatcherReadonlyComponent,
        BotManagementMatchComponent,
        BotManagementMatchReadonlyComponent,
        BotTypeMatcherComponent,
        BotTypeMatcherReadonlyComponent,
        HdrMatchComponent,
        HdrMatchReadonlyComponent,
        HostHdrMatchComponent,
        HostHdrMatchReadonlyComponent,
        HostHdrMatchWithObjectsComponent,
        HttpMethodMatchComponent,
        HttpMethodMatchReadonlyComponent,
        HttpStatusMatchComponent,
        HttpStatusMatchReadonlyComponent,
        HttpVersionMatchReadonlyComponent,
        IpAddrMatchComponent,
        IpAddrMatchEntryComponent,
        IpAddrMatchReadonlyComponent,
        IpReputationTypeMatchReadonlyComponent,
        JwtMatchReadonlyComponent,
        MatchAdderComponent,
        MatchAdderWithObjectsComponent,
        MatchAndActionDisplayWrapperComponent,
        MatchDisplayWrapperComponent,
        MatchWrapperComponent,
        MethodMatchWithObjectsComponent,
        PathMatchWithObjectsComponent,
        PathOrQueryMatchComponent,
        PathOrQueryMatchReadonlyComponent,
        PortMatchComponent,
        PortMatchReadonlyComponent,
        HttpVersionMatchReadonlyComponent,
        AuthMatchAttributeReadonlyComponent,
        AuthMatchGroupMembershipReadonlyComponent,
        StringMatchComponent,
        StringMatchConfigComponent,
        StringMatchReadonlyComponent,
        ProtocolMatchReadonlyComponent,
        ProtocolTypeMatchReadonlyComponent,
        GeoMatchComponent,
        GeoMatchReadonlyComponent,
        TlsFingerprintMatchReadonlyComponent,
    ],
    imports: [
        CommonModule,
        VIPModule,
        SharedModule,
        AviFormsModule,
        DataGridModule,
        AviComponentRenderingModule,
    ],
    providers: [
        ipAddrGroupCollectionProvider,
        rangeParserProvider,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
})
export class MatchModule {}
